import { Bell } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ProductNotificationMappingDialog from "./ProductNotificationMappingDialog";
import { useAuth } from "../../contexts/AuthContext";
import { useProductUpdateNotificationsEnabled } from "../../hooks/useProductUpdateNotificationsEnabled";
import { useProductNotificationMapping } from "../../hooks/useProductNotificationMapping";
import { isCustomerViewBootProfile } from "../../utils/adminCustomerViewBoot";
import { isNotificationPortfolioId } from "../../utils/productUpdateNotifications";
import { hasNotificationChannels, noProductNotificationChannels, selectionsFromMapping } from "../../utils/productNotificationSelections";
import activityIcon from "../../assets/notifications/trade-alert-activity.svg";
import { useToast } from "../../contexts/ToastContext";

interface Props {
  productType: string;
  productId: number;
  visibility: string;
  appearance?: "default" | "alpha";
  variant?: "inline" | "badge";
}

export default function ProductUpdateNotificationsButton(props: Props) {
  const enabled = useProductUpdateNotificationsEnabled();
  const { user } = useAuth();
  if (!enabled || !user?.id || isCustomerViewBootProfile() || (props.productType !== "PORTFOLIO" && props.productType !== "BASKET")
    || props.visibility === "PRIVATE" || !isNotificationPortfolioId(props.productId)) return null;
  // Unmounting on logout or actor/product changes invalidates pending reads and writes.
  return props.variant === "inline"
    ? <VisibleProductButton key={`${user.id}:${props.productType}:${props.productId}`} {...props} />
    : <ProductButton key={`${user.id}:${props.productType}:${props.productId}`} {...props} />;
}

function VisibleProductButton(props: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    if (typeof IntersectionObserver === "undefined") { setVisible(true); return; }
    // Discover can render a long catalog. Match the comment button's lazy
    // lookup so offscreen cards do not each request notification preferences.
    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setVisible(true);
        observer.disconnect();
      }
    }, { rootMargin: "200px" });
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return <div ref={ref} className="inline-flex">
    {visible ? <ProductButton {...props} /> : <span className="h-8 w-[164px]" aria-hidden="true" />}
  </div>;
}

function ProductButton({ productId, productType, appearance = "default", variant = "badge" }: Props) {
  const controller = useProductNotificationMapping(productId, productType === "BASKET" ? "BASKET" : "PORTFOLIO");
  const { state, saving, saveError, notice, reload, save } = controller;
  const [editing, setEditing] = useState(false);
  const isAlpha = appearance === "alpha";
  const compact = variant === "inline";
  const active = state.status === "ready" && hasNotificationChannels(selectionsFromMapping(state.value.mapping));
  const status = state.status === "ready" ? active ? "On" : "Off" : "Loading";
  const message = state.status === "error" ? state.message : saveError || notice;
  const buttonClass = `inline-flex shrink-0 items-center justify-center whitespace-nowrap font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${compact
    ? "h-8 gap-2 rounded-full border pl-2.5 pr-2 text-xs"
    : "h-14 gap-3 rounded-[10px] pl-[18px] pr-3.5 text-[15px] leading-[22.5px]"} ${isAlpha
    ? "border-v7-ink/15 bg-v7-white text-v7-ink focus-visible:outline-v7-bronze-dark hover:bg-v7-paper"
    : "border-gray-200 bg-white text-gray-900 hover:bg-gray-50 dark:border-rp-separator dark:bg-rp-surface dark:text-white dark:hover:bg-rp-surface-raised focus-visible:outline-rp-amber"}`;
  const track = <span aria-hidden="true" className={`relative inline-flex shrink-0 rounded-full transition-colors ${compact ? "h-5 w-9" : "h-[26px] w-11"} ${active
    ? isAlpha ? "bg-v7-ink" : "bg-rp-amber"
    : isAlpha ? "bg-v7-ink/20" : "bg-gray-300 dark:bg-gray-600"}`}>
    <span className={`absolute flex items-center justify-center rounded-full bg-white shadow-sm transition-transform motion-reduce:transition-none ${compact
      ? `left-[3px] top-[3px] h-3.5 w-3.5 ${active ? "translate-x-4" : "translate-x-0"}`
      : `left-[3px] top-[3px] h-5 w-5 ${active ? "translate-x-[18px]" : "translate-x-0"}`}`}>
      {active && !compact && <>
        <span className="absolute inset-0 rounded-full border border-v7-success motion-safe:animate-trade-alert-ring" />
        <img src={activityIcon} alt="" className="h-3 w-3 motion-safe:animate-trade-alert-breathe" />
      </>}
    </span>
  </span>;
  const content = <>{compact && <Bell className="h-3.5 w-3.5" aria-hidden="true" />}<span>Trade alerts</span>{track}</>;

  return <div className="inline-flex flex-col items-end gap-1" onClick={event => event.stopPropagation()}>
    {controller.autoSubscriptionMessage && <ShareSignInSubscriptionMessage message={controller.autoSubscriptionMessage} />}
    {state.status === "error" ? <button type="button" onClick={reload} className={buttonClass}>Retry trade alerts</button>
      : state.status !== "unavailable" && <button type="button" role="switch" aria-checked={active}
        aria-label={`Trade alerts: ${status}`} aria-busy={saving || state.status === "loading"}
        disabled={saving || state.status === "loading"}
        title={`Turn trade alerts ${active ? "off" : "on"}`}
        onClick={() => {
          if (active) void save(noProductNotificationChannels);
          else { reload(); setEditing(true); }
        }} className={buttonClass}>
        {content}
      </button>}
    {!editing && message && <p role={notice ? "status" : "alert"} className={`max-w-64 text-xs ${isAlpha ? "text-v7-rust" : "text-red-700 dark:text-red-400"}`}>{message}</p>}
    {editing && createPortal(<ProductNotificationMappingDialog defaultChannelsOn={productType === "PORTFOLIO"} controller={controller} onClose={() => { if (!saving) setEditing(false); }}
      onSaved={() => setEditing(false)} appearance={appearance} />, document.body)}
  </div>;
}

function ShareSignInSubscriptionMessage({ message }: { message: string }) {
  const { success, removeToast } = useToast();
  useEffect(() => {
    const id = success(message, 3000);
    return () => removeToast(id);
  }, [message, success, removeToast]);
  return null;
}
